import { resolveCustomTriggerStyleConfig } from './custom-trigger-style';
import {
    defaultFloatingColorFromDarkFlag,
    resolveFloatingButtonStyle,
} from './floating-launcher-style';
import type { ResolvedConfig, VotiqInitOptions, WidgetTab } from './types';

const DEFAULTS: Omit<ResolvedConfig, 'projectId' | 'container'> = {
    enabled: true,
    theme: 'light',
    position: 'bottom-right',
    buttonType: 'floating',
    floatingIcon: 'votiq',
    launcherIconPreset: null,
    customLauncherIconUrl: null,
    darkLauncher: false,
    launcherLabel: 'Feedback',
    welcomeTitle: 'Help shape what we build',
    welcomeMessage:
        'Share feedback, follow the roadmap, and vote on what ships next.',
    showFeedback: true,
    showRoadmap: true,
    showChangelog: true,
    showHelp: true,
    apiBaseUrl: '',
    preview: false,
    previewScope: 'full',
    customButton: resolveCustomTriggerStyleConfig(null),
    floatingButton: resolveFloatingButtonStyle({
        color: defaultFloatingColorFromDarkFlag(false),
    }),
};

export function resolveContainer(
    container?: HTMLElement | string,
): HTMLElement | null {
    if (!container) {
        return null;
    }

    if (typeof container === 'string') {
        return document.querySelector<HTMLElement>(container);
    }

    return container;
}

export function resolveConfig(options: VotiqInitOptions): ResolvedConfig {
    const tabs = options.tabs ?? {};

    return {
        projectId: options.projectId,
        enabled: options.enabled ?? DEFAULTS.enabled,
        theme: options.theme ?? DEFAULTS.theme,
        position: options.position ?? DEFAULTS.position,
        buttonType: options.buttonType ?? DEFAULTS.buttonType,
        floatingIcon: options.floatingIcon ?? DEFAULTS.floatingIcon,
        launcherIconPreset:
            options.launcherIconPreset ?? DEFAULTS.launcherIconPreset,
        customLauncherIconUrl:
            options.customLauncherIconUrl ?? DEFAULTS.customLauncherIconUrl,
        darkLauncher: options.darkLauncher ?? DEFAULTS.darkLauncher,
        launcherLabel: options.launcherLabel ?? DEFAULTS.launcherLabel,
        welcomeTitle: options.welcomeTitle ?? DEFAULTS.welcomeTitle,
        welcomeMessage: options.welcomeMessage ?? DEFAULTS.welcomeMessage,
        showFeedback: tabs.feedback ?? DEFAULTS.showFeedback,
        showRoadmap: tabs.roadmap ?? DEFAULTS.showRoadmap,
        showChangelog: tabs.changelog ?? DEFAULTS.showChangelog,
        showHelp: tabs.help ?? DEFAULTS.showHelp,
        apiBaseUrl: options.apiBaseUrl ?? DEFAULTS.apiBaseUrl,
        preview: options.preview ?? DEFAULTS.preview,
        previewScope: options.previewScope ?? DEFAULTS.previewScope,
        customButton: resolveCustomTriggerStyleConfig(
            options.customButton ?? DEFAULTS.customButton,
        ),
        floatingButton: resolveFloatingButtonStyle(
            options.floatingButton ?? {
                color: defaultFloatingColorFromDarkFlag(
                    options.darkLauncher ?? DEFAULTS.darkLauncher,
                ),
            },
        ),
        container: resolveContainer(options.container),
    };
}

function resolveCustomLauncherIconUrl(
    patch: Partial<VotiqInitOptions>,
    current: ResolvedConfig,
): string | null {
    if ('customLauncherIconUrl' in patch) {
        return patch.customLauncherIconUrl ?? null;
    }

    return current.customLauncherIconUrl;
}

export function mergeConfig(
    current: ResolvedConfig,
    patch: Partial<VotiqInitOptions>,
): ResolvedConfig {
    return resolveConfig({
        projectId: patch.projectId ?? current.projectId,
        enabled: patch.enabled ?? current.enabled,
        theme: patch.theme ?? current.theme,
        position: patch.position ?? current.position,
        buttonType: patch.buttonType ?? current.buttonType,
        floatingIcon: patch.floatingIcon ?? current.floatingIcon,
        launcherIconPreset:
            patch.launcherIconPreset !== undefined
                ? patch.launcherIconPreset
                : current.launcherIconPreset,
        customLauncherIconUrl: resolveCustomLauncherIconUrl(patch, current),
        darkLauncher: patch.darkLauncher ?? current.darkLauncher,
        launcherLabel: patch.launcherLabel ?? current.launcherLabel,
        welcomeTitle: patch.welcomeTitle ?? current.welcomeTitle,
        welcomeMessage: patch.welcomeMessage ?? current.welcomeMessage,
        tabs: {
            feedback: patch.tabs?.feedback ?? current.showFeedback,
            roadmap: patch.tabs?.roadmap ?? current.showRoadmap,
            changelog: patch.tabs?.changelog ?? current.showChangelog,
            help: patch.tabs?.help ?? current.showHelp,
        },
        apiBaseUrl: patch.apiBaseUrl ?? current.apiBaseUrl,
        container: patch.container ?? current.container,
        preview: patch.preview ?? current.preview,
        previewScope: patch.previewScope ?? current.previewScope,
        customButton: patch.customButton
            ? resolveCustomTriggerStyleConfig({
                  ...current.customButton,
                  ...patch.customButton,
              })
            : current.customButton,
        floatingButton: patch.floatingButton
            ? resolveFloatingButtonStyle({
                  ...current.floatingButton,
                  ...patch.floatingButton,
              })
            : current.floatingButton,
    });
}

export function enabledTabs(config: ResolvedConfig): WidgetTab[] {
    const tabs: WidgetTab[] = [];

    if (config.showHelp) {
        tabs.push('help');
    }

    if (config.showFeedback) {
        tabs.push('feedback');
    }

    if (config.showRoadmap) {
        tabs.push('roadmap');
    }

    if (config.showChangelog) {
        tabs.push('updates');
    }

    return tabs;
}
